import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../components/ui/Icon';
import { useIsMobile } from '../../hooks/useIsMobile';
import { fmtEur, fmtDate, euro } from '../../lib/utils';

/**
 * Popover "Prolunga il canone": cosa succede quando si porta a casa un rinnovo.
 *
 * Un rinnovo non apre una commessa nuova — sono gli stessi soldi, e aprirla li
 * conterebbe due volte. Qui si dice solo fino a quando vale il contratto, e se
 * il prezzo è cambiato.
 *
 * Le date NON hanno un default: la scadenza la decide chi ha trattato, non il
 * gestionale. Finché non è scritta, il pulsante resta spento.
 */
export function RenewPopover({ contratto, pending, onConfirm, onClose }: {
  contratto: { id: string; name: string; amountNet: number; cycle: string; endDate: string | null };
  pending?: boolean;
  onConfirm: (v: { endDate: string; amountNet?: number; startDate?: string }) => void;
  onClose: () => void;
}) {
  const [endDate, setEndDate] = useState('');
  const [prezzo, setPrezzo] = useState('');
  const [startDate, setStartDate] = useState('');
  const mobile = useIsMobile();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const nuovo = prezzo.trim() === '' ? null : Number(prezzo.replace(',', '.'));
  const prezzoValido = nuovo == null || (Number.isFinite(nuovo) && nuovo >= 0);
  const cambiaPrezzo = nuovo != null && prezzoValido && nuovo !== contratto.amountNet;
  const ok = !!endDate && prezzoValido && !pending;

  const submit = () => {
    if (!ok) return;
    onConfirm({
      endDate,
      ...(cambiaPrezzo ? { amountNet: nuovo! } : {}),
      ...(cambiaPrezzo && startDate ? { startDate } : {}),
    });
  };

  // Sul telefono è un foglio dal basso, in un portale: dentro la pagina il
  // bottone "Prolunga" finirebbe sotto la barra in basso. Vedi NextActionPopover.
  const corpo = (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div
        className="dropdown adm-pop right-0 mt-1 z-[61]"
        role="dialog"
        aria-label="Prolunga il canone"
        style={{ width: 'min(420px, calc(100vw - 32px))' }}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit(); }}
      >
        <div className="adm-pop-hd">
          <span className="t-micro">Prolunga</span>
          <span className="font-semibold truncate">{contratto.name}</span>
        </div>
        <div className="adm-pop-grid">
          <label className="field-label">Fino al</label>
          <input
            className="inp inp-sm mono"
            type="date"
            autoFocus
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <label className="field-label">Prezzo</label>
          <input
            className="inp inp-sm mono"
            inputMode="decimal"
            placeholder={`${fmtEur(contratto.amountNet)} — lascia vuoto se non cambia`}
            value={prezzo}
            onChange={(e) => setPrezzo(e.target.value)}
          />
          {cambiaPrezzo && (
            <>
              <label className="field-label">Dal</label>
              <input
                className="inp inp-sm mono"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </>
          )}
        </div>
        <div className="px-4 pb-3 text-[12px] mute" style={{ lineHeight: 1.45 }}>
          {cambiaPrezzo ? (
            <>
              Il canone di adesso si chiude{contratto.endDate ? ` il ${fmtDate(contratto.endDate)}` : ''} e ne nasce uno
              da {euro(nuovo!)}: le due righe non si sovrappongono mai, così il totale del mese resta quello vero.
            </>
          ) : (
            <>Stesso prezzo: è lo stesso contratto che continua, si sposta solo la scadenza. Niente progetto nuovo: sarebbero gli stessi soldi contati due volte.</>
          )}
        </div>
        <div className="adm-pop-ft">
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-sm" disabled={!ok} onClick={submit}>
            <Icon name="repeat" size={10} /> Prolunga
          </button>
        </div>
      </div>
    </>
  );
  return mobile ? createPortal(corpo, document.body) : corpo;
}
